import { useState } from 'react'
import { Rocket } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'

/**
 * Board 5q82: the top of the project's onboarding thread — says this chat is
 * where the project is set up, that HUB asks a few questions and that every
 * one can be skipped. Once HUB archived the thread it also offers to restore it.
 */
export function OnboardingBanner({
  projectId,
  threadId,
  archived,
  onRestored,
}: {
  projectId: string
  threadId: string
  archived: boolean
  onRestored: () => void
}) {
  const { t } = useTranslation('chat')
  const [restoring, setRestoring] = useState(false)

  const restore = async () => {
    setRestoring(true)
    try {
      await orpc.chat.updateThread({ projectId, threadId, archived: false })
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
      onRestored()
    } catch {
      /* the note stays; the user can try again */
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div
      data-slot="onboarding-banner"
      role="note"
      className="border-primary/60 border-l-primary bg-accent-soft mb-6 flex gap-3 rounded-xl border border-l-4 p-4"
    >
      <span className="bg-primary text-primary-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
        <Rocket className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-accent-ink text-sm font-semibold">{t('onboarding.bannerTitle')}</p>
        <p className="text-muted-foreground text-sm">{t('onboarding.bannerBody')}</p>
        {archived && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <p className="text-muted-foreground text-sm">{t('onboarding.archivedNote')}</p>
            <Button size="sm" variant="outline" onClick={restore} disabled={restoring}>
              {t('onboarding.unarchive')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
